// ** React Imports
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

// ** Third Party Components
import classnames from 'classnames'
import { ChevronDown, Search, Sidebar as SidebarIcon } from 'react-feather'

// ** Custom Components
import UserCard from './UserCard'

// Subtitle under the brand name in the sidebar header
const WORKSPACE_NAME = 'Training workspace'

const matches = (text, query) => text.toLowerCase().includes(query)

// Keeps the links whose title matches; a section whose own title matches
// keeps all of its links
const filterSection = (section, query) => {
  if (!query || matches(section.title, query)) return section
  const keep = (link) => matches(link.title, query)
  const links = section.links.filter(keep)
  if (!links.length) return null
  return {
    ...section,
    links,
    subgroups: section.subgroups
      .map((group) => ({ ...group, links: group.links.filter(keep) }))
      .filter((group) => group.links.length)
  }
}

const NavItem = ({ link, isActive, onNavigate }) => (
  <Link
    to={link.navLink}
    className={classnames('ntts-sidebar__item', { 'is-active': isActive })}
    data-label={link.title}
    aria-current={isActive ? 'page' : undefined}
    onClick={onNavigate}
  >
    {link.icon}
    <span className="ntts-sidebar__label">{link.title}</span>
  </Link>
)

const NavGroup = ({ section, open, active, onToggle, onNavigate }) => {
  const hasActive = active?.section.id === section.id
  return (
    <div className={classnames('ntts-sidebar__group', { 'is-open': open })}>
      <button
        type="button"
        className={classnames('ntts-sidebar__item', { 'has-active': hasActive })}
        data-label={section.title}
        aria-expanded={open}
        onClick={() => onToggle(section)}
      >
        {section.icon}
        <span className="ntts-sidebar__label">{section.title}</span>
        <ChevronDown className="ntts-sidebar__chevron" aria-hidden="true" />
      </button>
      {open && (
        <div className="ntts-sidebar__children">
          {section.subgroups.map((group) => (
            <Fragment key={group.title || 'links'}>
              {group.title && (
                <span className="ntts-sidebar__eyebrow">{group.title}</span>
              )}
              {group.links.map((link) => {
                const isActive = active?.link === link
                return (
                  <Link
                    key={link.navLink}
                    to={link.navLink}
                    className={classnames('ntts-sidebar__sublink', {
                      'is-active': isActive
                    })}
                    aria-current={isActive ? 'page' : undefined}
                    onClick={onNavigate}
                  >
                    {link.title}
                  </Link>
                )
              })}
            </Fragment>
          ))}
        </div>
      )}
    </div>
  )
}

export const Sidebar = ({
  nav,
  active,
  collapsed,
  onToggle,
  onExpand,
  onNavigate,
  skin,
  setSkin
}) => {
  const navigate = useNavigate()
  const searchRef = useRef(null)
  const [query, setQuery] = useState('')
  const [openIds, setOpenIds] = useState(
    () => new Set(active ? [active.section.id] : [])
  )

  // ** Keep the group of the current page open
  const activeSectionId = active?.section.id
  useEffect(() => {
    if (!activeSectionId) return
    setOpenIds((ids) =>
      ids.has(activeSectionId) ? ids : new Set(ids).add(activeSectionId)
    )
  }, [activeSectionId])

  // ** Ctrl/Cmd + K focuses "Jump to…"
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        onExpand()
        // Wait for the expanded sidebar to render the input
        setTimeout(() => searchRef.current?.focus(), 0)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onExpand])

  const q = query.trim().toLowerCase()
  const visible = useMemo(() => {
    const keep = (sections) =>
      sections.map((s) => filterSection(s, q)).filter(Boolean)
    return {
      links: nav.railLinks.filter((link) => !q || matches(link.title, q)),
      sections: keep(nav.sections.filter((s) => !s.pinBottom)),
      pinned: keep(nav.sections.filter((s) => s.pinBottom))
    }
  }, [nav, q])

  const toggleGroup = (section) => {
    if (collapsed) {
      onExpand()
      setOpenIds((ids) => new Set(ids).add(section.id))
      return
    }
    setOpenIds((ids) => {
      const next = new Set(ids)
      if (next.has(section.id)) next.delete(section.id)
      else next.add(section.id)
      return next
    })
  }

  const handleNavigate = () => {
    setQuery('')
    onNavigate()
  }

  const handleSearchKey = (e) => {
    if (e.key === 'Escape') {
      setQuery('')
      e.currentTarget.blur()
    } else if (e.key === 'Enter' && q) {
      const first =
        visible.links[0] ||
        [...visible.sections, ...visible.pinned].find((s) => s.links.length)
          ?.links[0]
      if (first) {
        navigate(first.navLink)
        handleNavigate()
      }
    }
  }

  const renderGroup = (section) => (
    <NavGroup
      key={section.id}
      section={section}
      // Searching shows every match, so matching groups are forced open
      open={!collapsed && (!!q || openIds.has(section.id))}
      active={active}
      onToggle={toggleGroup}
      onNavigate={handleNavigate}
    />
  )

  const empty =
    q && !visible.links.length && !visible.sections.length && !visible.pinned.length

  return (
    <aside className="ntts-sidebar" aria-label="Navigation">
      <div className="ntts-sidebar__head">
        <span className="ntts-sidebar__logo" aria-hidden="true">
          N
        </span>
        <span className="ntts-sidebar__brand">
          <span className="ntts-sidebar__brand-name">NTTS</span>
          <span className="ntts-sidebar__brand-sub">{WORKSPACE_NAME}</span>
        </span>
        <button
          type="button"
          className="ntts-sidebar__toggle"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          onClick={onToggle}
        >
          <SidebarIcon />
        </button>
      </div>

      {/* <label className="ntts-sidebar__search">
        <Search aria-hidden="true" />
        <input
          ref={searchRef}
          type="search"
          placeholder="Jump to…"
          aria-label="Jump to page"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleSearchKey}
        />
        <kbd>Ctrl K</kbd>
      </label> */}

      <nav className="ntts-sidebar__nav">
        {visible.links.map((link) => (
          <NavItem
            key={link.navLink}
            link={link}
            isActive={active?.link === link}
            onNavigate={handleNavigate}
          />
        ))}
        {visible.sections.map(renderGroup)}
        {empty && <span className="ntts-sidebar__empty">No pages match</span>}
      </nav>

      <div className="ntts-sidebar__foot">
        {visible.pinned.map(renderGroup)}
        <UserCard skin={skin} setSkin={setSkin} />
      </div>
    </aside>
  )
}

export default Sidebar
